import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataGrid,
  useDataGridColumnsState,
} from '@ibsys/kit';
import type { DataGridLeafColumn } from '@ibsys/kit';
import { QualityDetailItem } from '@/entities/dashboards';
import { EMPTY_RESULT, type ExpertReportResult } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { StatBox } from './parts/StatBox';
import {
  dashboardGridHeight,
  getBlockResult,
  getQualityResultText,
  getTotalResult,
  scoreClass,
  totalScoreClass,
} from './utils';

type Props = {
  call: QualityDetailItem | null;
  blockNames: string[];
  onClose: () => void;
  /** Открытие диалога выбранного звонка; `openInNewTab` — пользователь кликнул с shift */
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/** Строка таблицы критериев звонка */
type CriterionRow = {
  name: string;
  /** Результат критерия в том виде, в каком его дало правило расчёта блока */
  result: ExpertReportResult;
  /** Балл критерия; `null` — результат не балл. Задаёт зону качества строки */
  rating: number | null;
  comment: string;
};

/**
 * Детальная карточка оценки обращения: шапка с метаданными,
 * общий балл и построчная разбивка баллов и комментариев по критериям.
 */
export const CallDetailsCard: FC<Props> = ({ call, blockNames, onClose, onOpenConversation }) => {
  const { t } = useTranslation('qualityDashboard');

  return (
    <Card>
      <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <CardTitle>{t('callDetails.title')}</CardTitle>
        {call && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Обычный клик открывает диалог в текущей вкладке, shift+клик — в новой. */}
            <Button size="sm" onClick={(e) => onOpenConversation(call.conversationId, e.shiftKey)}>
              {t('callDetails.openDialog')}
            </Button>
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t('callDetails.close')}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {!call ? (
          <div className="text-muted-foreground text-sm">{t('callDetails.hint')}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <StatBox
                label={t('callDetails.date')}
                value={call.evaluationDate ?? '—'}
                valueClassName="text-base font-bold"
              />
              <StatBox
                label={t('callDetails.consultant')}
                value={call.consultantName}
                valueClassName="text-base font-bold"
              />
              <StatBox
                label={t('callDetails.client')}
                value={call.clientName}
                valueClassName="text-base font-bold"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <StatBox
                label={t('callDetails.externalId')}
                value={call.externalId}
                valueClassName="text-base font-bold"
              />
              <StatBox
                label={t('callDetails.totalScore')}
                value={
                  <span
                    className={cn(
                      'inline-block rounded-lg px-4 py-2 text-2xl font-bold',
                      totalScoreClass(call.totalRating),
                    )}
                  >
                    {getQualityResultText(getTotalResult(call)) ?? EMPTY_RESULT}
                  </span>
                }
                valueClassName="mt-1"
              />
            </div>
            <CallCriteriaTable call={call} blockNames={blockNames} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CallCriteriaTable: FC<{ call: QualityDetailItem; blockNames: string[] }> = ({
  call,
  blockNames,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const data = useMemo(
    (): CriterionRow[] =>
      blockNames.map((name) => {
        const block = call.blocks.find((b) => b.blockName === name);
        return {
          name,
          result: getBlockResult(block),
          rating: block?.rating ?? null,
          comment: block?.comment ?? '',
        };
      }),
    [call, blockNames],
  );

  const columns = useMemo((): DataGridLeafColumn<CriterionRow>[] => [
    {
      id: 'criterion',
      header: t('callDetails.columns.criterion'),
      accessor: 'name',
      size: 220,
    },
    {
      id: 'score',
      header: t('callDetails.columns.score'),
      // Результат критерия показывается таким, каким его дало правило расчёта: балл — баллом,
      // значение — значением, набор значений — перечислением
      accessor: (row) => getQualityResultText(row.result) ?? EMPTY_RESULT,
      size: 90,
      align: 'center',
      className: 'font-bold',
      // Зона качества есть только у балла: значение и набор долей от максимума не измеряются
      cellClassName: ({ row }) => scoreClass(row.rating),
      cell: ({ row }) => getQualityResultText(row.result) ?? EMPTY_RESULT,
    },
    {
      id: 'comment',
      header: t('callDetails.columns.comment'),
      accessor: 'comment',
      size: 350,
      wrap: true,
      cell: ({ value }) => (value as string) || '—',
    },
  ], [t]);

  const { gridProps, panelProps } = useDataGridColumnsState({
    defaultOrder: ['criterion', 'score', 'comment'],
    defaultPinning: { left: ['criterion'], right: [] },
  });

  const tableHeight = dashboardGridHeight(data.length);

  return (
    <DataGrid
      {...gridProps}
      columns={columns}
      dataSource={{ mode: 'client', data }}
      getRowId={(r) => r.name}
      enableColumnResizing
      enableColumnReordering
      enableColumnPinning
      columnsPanel={{ showPinning: true, onReset: panelProps.onReset }}
      height={tableHeight}
    />
  );
};
